import { ApolloError } from '@apollo/client';
import { ZonedDateTime, ZoneId } from '@js-joda/core';
import { useEffect, useMemo, useState } from 'react';
import {
  GetMboClassesByDateRangeQuery,
  MboClassDetailsFragment,
  useGetMboClassesByDateRangeLazyQuery,
} from '../graphql/types';
import { useClassesQueryVariables } from './useQueryParams';

export const PAGE_SIZE = 15;

interface MboClassesQueryResults {
  error: ApolloError | undefined;
  loading: boolean;
  initialLoading: boolean;
  isFetchingMore: boolean;
  hasNextPage: boolean | undefined;
  data: GetMboClassesByDateRangeQuery | undefined;
  classCount: number | undefined;
  classes: MboClassDetailsFragment[] | undefined;
  loadMoreClasses: () => Promise<void>;
}

export default (): MboClassesQueryResults => {
  const [{ locationIds, studioIds, dateRange }] = useClassesQueryVariables();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isChangingFilters, setIsChangingFilters] = useState(false);

  const inclusiveDateRange = useMemo(
    () => ({
      start: dateRange?.start
        ? ZonedDateTime.parse(dateRange.start)
            .withZoneSameInstant(ZoneId.SYSTEM)
            .withHour(0)
            .withMinute(0)
            .withSecond(0)
            .withFixedOffsetZone()
            .toJSON()
        : undefined,
      end: dateRange?.end
        ? ZonedDateTime.parse(dateRange.end)
            .withZoneSameInstant(ZoneId.SYSTEM)
            .withHour(23)
            .withMinute(59)
            .withSecond(59)
            .withFixedOffsetZone()
            .toJSON()
        : undefined,
    }),
    [dateRange],
  );

  const queryVariables = useMemo(
    () => ({
      filter: {
        locationIds,
        studioIds,
        dates: inclusiveDateRange,
      },
      input: {
        first: PAGE_SIZE,
      },
    }),
    [locationIds, studioIds, inclusiveDateRange],
  );

  const [, { data, loading, error, fetchMore, refetch: fetch }] =
    useGetMboClassesByDateRangeLazyQuery({
      fetchPolicy: 'cache-first',
      variables: queryVariables,
    });

  useEffect(() => {
    setIsChangingFilters(true);
    fetch(queryVariables).finally(() => {
      setIsChangingFilters(false);
    });
  }, [fetch, queryVariables]);

  const hasNextPage = data?.availableClasses?.pageInfo?.hasNextPage;

  const loadMoreClasses = async () => {
    if (!hasNextPage) {
      setIsFetchingMore(false);
      return;
    }
    setIsFetchingMore(true);

    await fetchMore({
      variables: {
        filter: {
          locationIds,
          studioIds,
          dates: inclusiveDateRange,
        },
        input: {
          first: PAGE_SIZE,
          after: data?.availableClasses?.pageInfo?.endCursor,
        },
      },
    });
    setIsFetchingMore(false);
  };

  const classCount = data?.availableClasses.count;

  const classes = data?.availableClasses?.nodes;

  const initialLoading = loading && (!classes || classes.length === 0);

  return {
    error,
    loading: loading || isChangingFilters,
    initialLoading,
    isFetchingMore,
    hasNextPage,
    data,
    classCount,
    classes,
    loadMoreClasses,
  };
};
